import React, { useEffect, useMemo, useState } from 'react';
import _noop from 'lodash/noop';

import { useTranslation } from '@lyearn/i18n';
import { EmptyState, EmptyStateActionType } from '@lyearn/molecules';
import { cn } from '@lyearn/style';
import { Box } from '@lyearn/ui';
import { getAppliedFilterCount } from '@/components/FilterManager/utils';

import { SearchListEmptyStateContainerStyles } from './styles';
import { SearchListEmptyStateProps, SearchListEmptyStateType } from './types';
import { getSearchListEmptyStateLabels } from './utils';

function SearchListEmptyState({
  className,
  title,
  description,
  filter,
  search,
  clearSearch,
  clearFilters,
  Icon,
  action,
  width,
  doNotUseFooter,
  size,
}: SearchListEmptyStateProps) {
  const { t } = useTranslation();
  const isFiltersApplied = getAppliedFilterCount(filter ?? {}) > 0;
  const isSearchApplied = (search?.length ?? 0) > 0;
  const showActionFooter = !isFiltersApplied && !isSearchApplied && !!doNotUseFooter;
  const [emptyStateType, setEmptyStateType] = useState(SearchListEmptyStateType.Content);

  const emptyStateContent = useMemo(
    () =>
      action
        ? {
            [SearchListEmptyStateType.Content]: {
              label: action?.label,
              onClick: action?.onClick,
            },
          }
        : undefined,
    [action],
  );

  const emptyStateActionMap = useMemo(
    () => ({
      [SearchListEmptyStateType.Filter]: {
        onClick: clearFilters,
        label: t('Common:EmptyState.ClearFilters'),
      },
      [SearchListEmptyStateType.Search]: {
        onClick: clearSearch,
        label: t('Common:EmptyState.ClearSearch'),
      },
      ...emptyStateContent,
    }),
    [clearFilters, clearSearch, emptyStateContent, t],
  );

  useEffect(() => {
    if (isFiltersApplied) {
      setEmptyStateType(SearchListEmptyStateType.Filter);

      return;
    }

    if (isSearchApplied) {
      setEmptyStateType(SearchListEmptyStateType.Search);

      return;
    }

    setEmptyStateType(SearchListEmptyStateType.Content);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter]);

  useEffect(() => {
    if (isSearchApplied) {
      setEmptyStateType(SearchListEmptyStateType.Search);

      return;
    }

    if (isFiltersApplied) {
      setEmptyStateType(SearchListEmptyStateType.Filter);

      return;
    }

    setEmptyStateType(SearchListEmptyStateType.Content);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  const {
    title: resolvedTitle,
    description: resolvedDescription,
    Icon: resolvedIcon,
  } = getSearchListEmptyStateLabels({
    title,
    description,
    Icon,
    type: emptyStateType,
    searchText: search ?? '',
    t,
  });

  const {
    primaryAction,
    secondaryAction,
  }: {
    primaryAction?: EmptyStateActionType;
    secondaryAction?: EmptyStateActionType;
  } = useMemo(() => {
    if (
      emptyStateType === SearchListEmptyStateType.Filter ||
      emptyStateType === SearchListEmptyStateType.Search ||
      (emptyStateContent && emptyStateType === SearchListEmptyStateType.Content)
    ) {
      return {
        primaryAction: showActionFooter
          ? undefined
          : {
              label: emptyStateActionMap[emptyStateType]?.label ?? '',
              onClick: emptyStateActionMap[emptyStateType]?.onClick ?? _noop,
            },
        secondaryAction: undefined,
      };
    }

    return { primaryAction: undefined, secondaryAction: undefined, aiAction: undefined };
  }, [emptyStateActionMap, emptyStateContent, emptyStateType, showActionFooter]);

  return (
    <Box
      className={cn(
        'flex flex-1 flex-col items-center pb-4', // pb-2 to prevent button outline focus from bottom
        SearchListEmptyStateContainerStyles,
        className,
      )}>
      <EmptyState
        Icon={resolvedIcon}
        description={resolvedDescription}
        doNotUseFooter={showActionFooter ? doNotUseFooter : undefined}
        primaryAction={primaryAction}
        secondaryAction={secondaryAction}
        size={size}
        title={resolvedTitle}
        width={width}
      />
    </Box>
  );
}

export default React.memo(SearchListEmptyState);
